import { useCallback, useEffect, useState } from "react";

export type RecentSearch = {
  query: string;
  docCount: number;
};

const MAX_ENTRIES = 10;

const storageKey = (caseNumber: string) => `recent-searches:${caseNumber}`;

const load = (caseNumber: string): RecentSearch[] => {
  try {
    const raw = localStorage.getItem(storageKey(caseNumber));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Per-case localStorage-backed history of semantic searches. `docCount` is the
// number of results the search returned, surfaced as the "N docs" count in the
// dropdown. There is no backend persistence (frontend-only by design).
export const useRecentSearches = (caseNumber: string) => {
  const [items, setItems] = useState<RecentSearch[]>(() => load(caseNumber));

  // Re-sync when the hook stays mounted across a case change.
  useEffect(() => {
    setItems(load(caseNumber));
  }, [caseNumber]);

  const add = useCallback(
    (query: string, docCount: number) => {
      const trimmed = query.trim();
      if (!trimmed) return;

      setItems((prev) => {
        const next = [
          { query: trimmed, docCount },
          ...prev.filter((item) => item.query !== trimmed),
        ].slice(0, MAX_ENTRIES);

        try {
          localStorage.setItem(storageKey(caseNumber), JSON.stringify(next));
        } catch {
          // Ignore quota / unavailable storage — history is best-effort.
        }
        return next;
      });
    },
    [caseNumber]
  );

  return { items, add };
};
